/**
 * TopBar — persistent header with logo, Studio button, and auth pill.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Key, LogIn, LogOut, Paintbrush } from 'lucide-react';
import type { NostrProfile } from '@/hooks/useNostrLogin';

interface TopBarProps {
  pubkey: string | null;
  profile: NostrProfile | null;
  isLoading: boolean;
  isExtensionAvailable: boolean;
  onLoginExtension: () => Promise<void>;
  onLoginNsec: (nsec: string) => Promise<void>;
  onLogout: () => void;
}

export function TopBar({
  pubkey,
  profile,
  isLoading,
  isExtensionAvailable,
  onLoginExtension,
  onLoginNsec,
  onLogout,
}: TopBarProps) {
  const navigate = useNavigate();
  const isLoggedIn = !!pubkey;
  const hasNip05 = !!profile?.nip05;

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4">
        {/* Left: Logo */}
        <button
          onClick={() => navigate('/')}
          className="text-lg font-bold tracking-tight hover:opacity-80 transition-opacity"
        >
          Tile Studio
        </button>

        {/* Right: Studio + Auth */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/editor')}
            disabled={!isLoggedIn || !hasNip05}
            title={!isLoggedIn ? 'Log in to access the studio' : !hasNip05 ? 'NIP-05 required to publish tiles' : undefined}
            className="gap-1.5"
          >
            <Paintbrush className="size-3.5" />
            Studio
          </Button>

          {isLoggedIn ? (
            <LoggedInPill profile={profile} onLogout={onLogout} />
          ) : (
            <LoginPill
              isLoading={isLoading}
              isExtensionAvailable={isExtensionAvailable}
              onLoginExtension={onLoginExtension}
              onLoginNsec={onLoginNsec}
            />
          )}
        </div>
      </div>
    </header>
  );
}

/** Logged-in: avatar + name pill with logout dropdown */
function LoggedInPill({
  profile,
  onLogout,
}: {
  profile: NostrProfile | null;
  onLogout: () => void;
}) {
  const displayName = profile?.display_name || profile?.name || 'anon';
  const avatar = profile?.picture;

  return (
    <Popover>
      <PopoverTrigger>
        <div className="flex items-center gap-2 rounded-full border border-border px-2.5 py-1.5 text-sm hover:bg-accent transition-colors cursor-pointer">
          {avatar ? (
            <img
              src={avatar}
              alt=""
              className="size-5 rounded-full object-cover"
            />
          ) : (
            <div className="size-5 rounded-full bg-muted" />
          )}
          <span className="max-w-[100px] truncate text-xs font-medium">
            {displayName}
          </span>
        </div>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-48">
        <div className="flex flex-col gap-1">
          {profile?.nip05 && (
            <p className="text-xs text-muted-foreground font-mono px-2 py-1 truncate">
              {profile.nip05}
            </p>
          )}
          <button
            onClick={onLogout}
            className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-destructive hover:bg-destructive/10 transition-colors w-full text-left"
          >
            <LogOut className="size-3.5" />
            Log out
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Logged-out: Login button that opens a popover with nsec + extension */
function LoginPill({
  isLoading,
  isExtensionAvailable,
  onLoginExtension,
  onLoginNsec,
}: {
  isLoading: boolean;
  isExtensionAvailable: boolean;
  onLoginExtension: () => Promise<void>;
  onLoginNsec: (nsec: string) => Promise<void>;
}) {
  const [nsecInput, setNsecInput] = useState('');
  const [nsecError, setNsecError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const handleNsecLogin = async () => {
    const nsec = nsecInput.trim();
    if (!nsec) return;
    if (!/^nsec1[a-z0-9]{58}$/.test(nsec)) {
      setNsecError('Invalid nsec format');
      return;
    }
    setNsecError(null);
    try {
      await onLoginNsec(nsec);
      setOpen(false);
      setNsecInput('');
    } catch (err) {
      setNsecError(err instanceof Error ? err.message : 'Login failed');
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger>
        <span className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground shadow-xs hover:bg-primary/90 cursor-pointer">
          <LogIn className="size-3.5" />
          Login
        </span>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72">
        <div className="flex flex-col gap-3">
          <p className="text-xs text-muted-foreground">
            Sign in with your Nostr key to access the studio.
          </p>

          {/* nsec input */}
          <div className="space-y-1.5">
            <Input
              type="password"
              value={nsecInput}
              onChange={(e) => { setNsecInput(e.target.value); setNsecError(null); }}
              onKeyDown={(e) => e.key === 'Enter' && handleNsecLogin()}
              placeholder="nsec1..."
              className="text-xs font-mono"
            />
            {nsecError && (
              <p className="text-xs text-destructive">{nsecError}</p>
            )}
            <Button
              onClick={handleNsecLogin}
              disabled={isLoading || !nsecInput.trim()}
              size="sm"
              className="gap-1.5 w-full"
            >
              <Key className="size-3.5" />
              {isLoading ? 'Connecting...' : 'Login with nsec'}
            </Button>
          </div>

          {/* Extension login */}
          {isExtensionAvailable && (
            <>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-px bg-border" />
                <span className="text-[10px] text-muted-foreground">or</span>
                <div className="flex-1 h-px bg-border" />
              </div>
              <Button
                onClick={async () => { await onLoginExtension(); setOpen(false); }}
                disabled={isLoading}
                variant="outline"
                size="sm"
                className="gap-1.5 w-full"
              >
                <LogIn className="size-3.5" />
                {isLoading ? 'Connecting...' : 'Use Extension'}
              </Button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
